import React, { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Home,
  FileText,
  Globe,
  Settings,
  Layout,
  Menu,
  Star,
  Copy,
  Archive
} from 'lucide-react'
import { StaticPage } from '@/types'
import { mockStaticPages } from '@/lib/mock-data'

export default function StaticPagesManagement() {
  const [pages, setPages] = useState<StaticPage[]>(mockStaticPages)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'published' | 'draft' | 'archived'>('all')
  const [templateFilter, setTemplateFilter] = useState<'all' | 'default' | 'about' | 'contact' | 'landing' | 'legal'>('all')

  const filteredPages = pages.filter(page => {
    const matchesSearch = page.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         page.slug.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         page.content.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || page.status === statusFilter
    const matchesTemplate = templateFilter === 'all' || page.template === templateFilter
    return matchesSearch && matchesStatus && matchesTemplate
  })

  const getStatusColor = (status: StaticPage['status']) => {
    switch (status) {
      case 'published': return 'bg-green-100 text-green-800'
      case 'draft': return 'bg-yellow-100 text-yellow-800'
      case 'archived': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTemplateColor = (template: StaticPage['template']) => {
    switch (template) {
      case 'landing': return 'bg-purple-100 text-purple-800'
      case 'about': return 'bg-blue-100 text-blue-800'
      case 'contact': return 'bg-green-100 text-green-800'
      case 'legal': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getTemplateIcon = (template: StaticPage['template']) => {
    switch (template) {
      case 'landing': return <Star className="h-3 w-3" />
      case 'about': return <FileText className="h-3 w-3" />
      case 'contact': return <Settings className="h-3 w-3" />
      case 'legal': return <Archive className="h-3 w-3" />
      default: return <Layout className="h-3 w-3" />
    }
  }

  const handleDelete = (id: string) => {
    const page = pages.find(p => p.id === id)
    if (page?.is_homepage) {
      alert('Cannot delete the homepage. Please set another page as homepage first.')
      return
    }
    if (confirm('Are you sure you want to delete this page? This action cannot be undone.')) {
      setPages(pages.filter(page => page.id !== id))
    }
  }

  const handleDuplicate = (id: string) => {
    const page = pages.find(p => p.id === id)
    if (page) {
      const duplicatedPage: StaticPage = {
        ...page,
        id: Date.now().toString(),
        title: `${page.title} (Copy)`,
        slug: `${page.slug}-copy`,
        status: 'draft',
        is_homepage: false,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
      setPages([...pages, duplicatedPage])
    }
  }

  const stats = {
    total: pages.length,
    published: pages.filter(p => p.status === 'published').length,
    draft: pages.filter(p => p.status === 'draft').length,
    homepage: pages.filter(p => p.is_homepage).length,
    navigation: pages.filter(p => p.show_in_nav).length
  }

  const templates = [
    { value: 'default', label: 'Default', count: pages.filter(p => p.template === 'default').length },
    { value: 'landing', label: 'Landing', count: pages.filter(p => p.template === 'landing').length },
    { value: 'about', label: 'About', count: pages.filter(p => p.template === 'about').length },
    { value: 'contact', label: 'Contact', count: pages.filter(p => p.template === 'contact').length },
    { value: 'legal', label: 'Legal', count: pages.filter(p => p.template === 'legal').length }
  ]

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Static Pages Management</h1>
          <p className="text-muted-foreground">
            Create and manage website pages with custom templates and content.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline">
            <Layout className="mr-2 h-4 w-4" />
            Page Builder
          </Button>
          <Button>
            <Plus className="mr-2 h-4 w-4" />
            New Page
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Pages</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              All static pages
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Published</CardTitle>
            <Globe className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.published}</div>
            <p className="text-xs text-muted-foreground">
              Live on website
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Drafts</CardTitle>
            <Edit className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.draft}</div>
            <p className="text-xs text-muted-foreground">
              Work in progress
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Homepage</CardTitle>
            <Home className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.homepage}</div>
            <p className="text-xs text-muted-foreground">
              Default landing page
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">In Navigation</CardTitle>
            <Menu className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.navigation}</div>
            <p className="text-xs text-muted-foreground">
              Visible in menu
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Main Content Area */}
        <div className="lg:col-span-3 space-y-6">
          {/* Filters and Search */}
          <Card>
            <CardHeader>
              <CardTitle>Page Directory</CardTitle>
              <CardDescription>
                Manage website pages, templates, and navigation structure.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-1 gap-2">
                  <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search pages..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value as any)}
                    className="px-3 py-2 border border-input rounded-md bg-background text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="archived">Archived</option>
                  </select>
                  <select
                    value={templateFilter}
                    onChange={(e) => setTemplateFilter(e.target.value as any)}
                    className="px-3 py-2 border border-input rounded-md bg-background text-sm"
                  >
                    <option value="all">All Templates</option>
                    <option value="default">Default</option>
                    <option value="landing">Landing</option>
                    <option value="about">About</option>
                    <option value="contact">Contact</option>
                    <option value="legal">Legal</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    <Filter className="mr-2 h-4 w-4" />
                    More Filters
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Pages Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Page</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Template</TableHead>
                    <TableHead>Navigation</TableHead>
                    <TableHead>SEO Score</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPages.map((page) => (
                    <TableRow key={page.id}>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium flex items-center gap-2">
                            {page.is_homepage && (
                              <Home className="h-4 w-4 text-blue-500" />
                            )}
                            {page.title}
                          </div>
                          <div className="text-sm text-muted-foreground line-clamp-1">
                            {page.excerpt || page.content.substring(0, 100) + '...'}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            /{page.slug}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getStatusColor(page.status)}>
                          {page.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={getTemplateColor(page.template)}>
                          <div className="flex items-center gap-1">
                            {getTemplateIcon(page.template)}
                            {page.template}
                          </div>
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {page.show_in_nav ? (
                            <div className="flex items-center gap-1">
                              <Menu className="h-4 w-4 text-green-500" />
                              <span className="text-sm">Menu #{page.menu_order}</span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">Hidden</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-12 h-2 bg-muted rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-green-500" 
                              style={{ width: '88%' }}
                            />
                          </div>
                          <span className="text-sm text-muted-foreground">88%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-muted-foreground">
                          {new Date(page.updated_at).toLocaleDateString()}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8"
                            onClick={() => handleDuplicate(page.id)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 text-destructive"
                            onClick={() => handleDelete(page.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Templates */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Page Templates</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {templates.map((template) => (
                <div key={template.value} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getTemplateIcon(template.value as StaticPage['template'])}
                    <span className="text-sm">{template.label}</span>
                  </div>
                  <Badge variant="outline" className="text-xs">
                    {template.count}
                  </Badge>
                </div>
              ))}
              <Button variant="ghost" size="sm" className="w-full mt-2">
                <Plus className="mr-2 h-4 w-4" />
                Custom Template
              </Button>
            </CardContent>
          </Card>

          {/* Navigation Structure */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Navigation Menu</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {pages
                  .filter(p => p.show_in_nav)
                  .sort((a, b) => a.menu_order - b.menu_order)
                  .map((page) => (
                    <div key={page.id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <Menu className="h-3 w-3 text-muted-foreground" />
                        <span>{page.title}</span>
                      </div>
                      <span className="text-muted-foreground">{page.menu_order}</span>
                    </div>
                  ))}
              </div>
              <Button variant="outline" size="sm" className="w-full mt-3">
                <Settings className="mr-2 h-4 w-4" />
                Manage Menu
              </Button>
            </CardContent>
          </Card>

          {/* Page Hierarchy */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Page Hierarchy</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="text-sm">
                  <div className="font-medium">Root Pages</div>
                  <div className="text-muted-foreground">
                    {pages.filter(p => !p.parent_id).length} pages
                  </div>
                </div>
                <div className="text-sm">
                  <div className="font-medium">Child Pages</div>
                  <div className="text-muted-foreground">
                    {pages.filter(p => p.parent_id).length} pages
                  </div>
                </div>
                <div className="text-sm">
                  <div className="font-medium">Max Depth</div>
                  <div className="text-muted-foreground">2 levels</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button variant="outline" size="sm" className="w-full justify-start">
                  <Layout className="mr-2 h-4 w-4" />
                  Page Builder
                </Button>
                <Button variant="outline" size="sm" className="w-full justify-start">
                  <Settings className="mr-2 h-4 w-4" />
                  Site Settings
                </Button>
                <Button variant="outline" size="sm" className="w-full justify-start">
                  <Globe className="mr-2 h-4 w-4" />
                  Preview Site
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}